/**
 * NovelForm Component
 * Reusable form for creating and editing novels
 */
import { useState, useEffect, type FormEvent, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Novel, NovelStatus } from "@novelreader/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NovelFormData {
  title: string;
  slug: string;
  description: string;
  status: NovelStatus;
}

export interface NovelFormProps {
  initialData?: Novel;
  onSubmit: (data: NovelFormData) => void;
  onCancel?: () => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: NovelStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
  { value: "hiatus", label: "Hiatus" },
];

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Generate a URL-friendly slug from a title
 */
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NovelForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: NovelFormProps) {
  const navigate = useNavigate();
  const isEditMode = Boolean(initialData);

  // Form state
  const [title, setTitle] = useState(initialData?.title ?? "");
  const [slug, setSlug] = useState(initialData?.slug ?? "");
  const [description, setDescription] = useState(
    initialData?.description ?? "",
  );
  const [status, setStatus] = useState<NovelStatus>(
    initialData?.status ?? "draft",
  );

  // Validation state
  const [errors, setErrors] = useState<{ title?: string }>({});

  // Track if slug has been manually edited (only in create mode)
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Auto-generate slug from title (only in create mode and if not manually edited)
  useEffect(() => {
    if (!isEditMode && !slugManuallyEdited) {
      setSlug(generateSlug(title));
    }
  }, [title, isEditMode, slugManuallyEdited]);

  // Clear title error when user types
  useEffect(() => {
    if (title && errors.title) {
      setErrors((prev) => ({ ...prev, title: undefined }));
    }
  }, [title, errors.title]);

  function handleTitleChange(e: ChangeEvent<HTMLInputElement>) {
    setTitle(e.target.value);
  }

  function handleSlugChange(e: ChangeEvent<HTMLInputElement>) {
    setSlug(e.target.value);
    if (!isEditMode) {
      setSlugManuallyEdited(true);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    // Validate
    if (!title.trim()) {
      setErrors({ title: "Title is required" });
      return;
    }

    onSubmit({
      title: title.trim(),
      slug: slug.trim(),
      description: description.trim(),
      status,
    });
  }

  function handleCancel() {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/novels");
    }
  }

  const submitButtonText = isLoading
    ? isEditMode
      ? "Updating..."
      : "Creating..."
    : isEditMode
      ? "Update Novel"
      : "Create Novel";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title Field */}
      <div>
        <label
          htmlFor="title"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Title
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={handleTitleChange}
          className={`w-full px-3 py-2 border rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
            errors.title ? "border-red-500" : "border-slate-300"
          }`}
          placeholder="Enter novel title"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
        )}
      </div>

      {/* Slug Field */}
      <div>
        <label
          htmlFor="slug"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Slug
        </label>
        <input
          id="slug"
          type="text"
          value={slug}
          onChange={handleSlugChange}
          className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          placeholder="url-friendly-slug"
        />
        <p className="mt-1 text-xs text-slate-500">
          URL-friendly identifier. Auto-generated from title.
        </p>
      </div>

      {/* Description Field */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Description
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
          placeholder="Enter novel description"
        />
      </div>

      {/* Status Field */}
      <div>
        <label
          htmlFor="status"
          className="block text-sm font-medium text-slate-700 mb-1"
        >
          Status
        </label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStatus(e.target.value as NovelStatus)}
          className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={handleCancel}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitButtonText}
        </button>
      </div>
    </form>
  );
}
